import { type Dayjs } from 'dayjs'
import {
  useMemo,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { getGroupedEvents } from '../utils/getGroupedEvents'
import { calculateOffsetAndHeight } from '../utils/event'
import { useAllGoogleCalendarEvents } from '../../../../contexts/GoogleCalendar'
import { type CalendarEvent } from '../../CalendarEvent'
import { type PlaceholderEvent } from '../types'
import { SingleEvent } from './SingleEvent'
import { MultipleEvents } from './MultipleEvents'
import { useAppleEvents } from '../../../../hooks/AppleCalDAV'
import { useLocalStorage } from 'usehooks-ts'
import { useOutlookEvents } from '../../../../hooks/OutlookCalendar'
import { useQueryClient } from '@tanstack/react-query'
import { googleCalendarDataContext } from '../../../../contexts/GoogleCalendar/persister'
import { cachedNotesContext } from '../../../../contexts/CachedNotes'
import { cacheKeys } from '../../../../utils/queryKeyFactory'
import { useAuthenticatedUser } from '../../../../providers/UserProvider'

export type CalendarEventsProps = {
  setCalendarEventDrag: Dispatch<SetStateAction<CalendarEvent | null>>
  selectedDay: Dayjs
  currentEvents: CalendarEvent[]
  placeholderEvent: PlaceholderEvent | null
}

export function CalendarEvents({
  setCalendarEventDrag, // TODO refactor into a context to avoid massive re-renderings through dragging
  selectedDay,
  currentEvents,
  placeholderEvent,
}: CalendarEventsProps) {
  const parent = document.querySelector('#week-columns')
  let segmentHeight = 28
  if (parent) {
    segmentHeight = parent.getBoundingClientRect().height / 97
  }

  const [hiddenCalendars] = useLocalStorage<string[]>('hiddenCalendars', [])
  const googleQueryClient = useQueryClient({
    context: googleCalendarDataContext,
  })
  const cachedNotesQueryClient = useQueryClient({ context: cachedNotesContext })
  const user = useAuthenticatedUser()
  const previousDayReference = useRef<string | null>(null)

  const currentEventsToday = currentEvents.filter((event) =>
    event.startDate.isSame(selectedDay, 'day')
  )
  const {
    data: googleEvents = [],
    isLoading: googleIsLoading,
    isRefetching: googleIsRefetching,
  } = useAllGoogleCalendarEvents(selectedDay)

  const appleResults = useAppleEvents(selectedDay)
  const appleIsFetching = appleResults.some((result) => result.isFetching)
  const appleIsRefetching = appleResults.some((result) => result.isRefetching)
  const appleEvents = useMemo(() => {
    return appleResults
      .flatMap((result) => result.data ?? [])
      .filter((event) => {
        if (event === undefined) return false

        return (
          event.startDate.isSame(selectedDay, 'day') ||
          event.endDate.isSame(selectedDay, 'day') ||
          (event.startDate.isBefore(selectedDay, 'day') &&
            event.endDate.isAfter(selectedDay, 'day'))
        )
      })
  }, [appleResults, selectedDay])

  const outlookResults = useOutlookEvents(selectedDay)
  const outlookIsFetching = outlookResults.some((result) => result.isFetching)
  const outlookIsRefetching = outlookResults.some(
    (result) => result.isRefetching
  )
  const outlookEvents = outlookResults.flatMap((result) => result.data ?? [])

  const handleRefreshCalendars = useCallback(() => {
    const dateKey = selectedDay.format('YYYYMMDD')
    const month = selectedDay.format('YYYY-MM')

    // Force refetch Google Calendar event queries for the current day
    // Google query keys: ['events', userId, accountId, calendarId, date]
    void googleQueryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as string[]
        return queryKey[0] === 'events' && queryKey.includes(dateKey)
      },
    })

    // Also refetch Google calendars to refresh the list
    void googleQueryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as string[]
        return queryKey[0] === 'calendars'
      },
    })

    // Force refetch Apple Calendar event queries for the current month
    // Apple query keys: ['cache', 'calendars', 'apple', userId, url, month]
    void cachedNotesQueryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as string[]
        return (
          queryKey[0] === 'cache' &&
          queryKey[1] === 'calendars' &&
          queryKey[2] === 'apple' &&
          queryKey.includes(month)
        )
      },
    })

    // Also force refetch Apple calendars list
    void cachedNotesQueryClient.refetchQueries({
      queryKey: cacheKeys.appleCalendars(user.userId),
    })

    // Force refetch Outlook Calendar queries
    // Outlook query keys: ['cache', 'calendars', 'outlook', userId, calendarId, day]
    void cachedNotesQueryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as string[]
        return (
          queryKey[0] === 'cache' &&
          queryKey[1] === 'calendars' &&
          queryKey[2] === 'outlook' &&
          queryKey.includes(dateKey)
        )
      },
    })

    // Also force refetch Outlook calendars list
    void cachedNotesQueryClient.refetchQueries({
      queryKey: cacheKeys.outlookCalendars(user.userId),
    })
  }, [googleQueryClient, cachedNotesQueryClient, selectedDay, user.userId])

  // Refresh events when the selected day changes
  useEffect(() => {
    const currentDayKey = selectedDay.format('YYYYMMDD')
    if (
      previousDayReference.current &&
      previousDayReference.current !== currentDayKey
    ) {
      // Day has changed, trigger refresh
      handleRefreshCalendars()
    }
    previousDayReference.current = currentDayKey
  }, [selectedDay, handleRefreshCalendars])

  const groupedEvents = useMemo(() => {
    const filteredEvents = [
      ...currentEventsToday,
      ...googleEvents,
      ...appleEvents,
      ...outlookEvents,
    ].filter((event): event is CalendarEvent => {
      return (
        event !== undefined &&
        !event.description?.startsWith('NPTB:') &&
        !event.allDay &&
        !hiddenCalendars.includes(event.calendarId ?? '')
      )
    })

    return getGroupedEvents(filteredEvents)
  }, [
    currentEventsToday,
    googleEvents,
    appleEvents,
    outlookEvents,
    hiddenCalendars,
  ])

  const eventElements = useMemo(() => {
    return groupedEvents
      .filter((group) => group.length > 0)
      .map((group) => {
        if (group.length === 1) {
          return (
            <SingleEvent
              key={`event-${group[0].id}`}
              event={group[0]}
              segmentHeight={segmentHeight}
              setCalendarEventDrag={setCalendarEventDrag}
              selectedDay={selectedDay}
            />
          )
        }
        return (
          <MultipleEvents
            key={`event-group-${group[0].startDate.toISOString()}`}
            events={group}
            segmentHeight={segmentHeight}
            setCalendarEventDrag={setCalendarEventDrag}
            selectedDay={selectedDay}
          />
        )
      })
  }, [groupedEvents, segmentHeight, selectedDay, setCalendarEventDrag])

  if (placeholderEvent) {
    const { eventHeight, offsetPxTop } = calculateOffsetAndHeight(
      placeholderEvent,
      segmentHeight,
      selectedDay
    )

    if (!Number.isNaN(eventHeight)) {
      eventElements.push(
        <li
          key='event-placeholder'
          id='event-placeholder'
          className='col-start-1 flex rounded-lg border-2 border-orange-500'
          style={{
            gridRow: placeholderEvent.position,
            transform: `translateY(${offsetPxTop.toString()}px)`,
            height: eventHeight - 2,
          }}
        />
      )
    }
  }

  const isLoading =
    googleIsLoading ||
    googleIsRefetching ||
    appleIsFetching ||
    appleIsRefetching ||
    outlookIsFetching ||
    outlookIsRefetching

  eventElements.push(
    <div
      key='loading-refresh'
      className='sticky top-0 z-20 mr-0 pr-1 text-right'
    >
      <button
        onClick={handleRefreshCalendars}
        disabled={isLoading}
        className='inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300'
        title='Refresh calendars'
      >
        {isLoading ? (
          <i className='fa-solid fa-circle-notch fa-spin text-sm text-blue-500 dark:text-blue-400' />
        ) : (
          <i className='fa-solid fa-arrows-rotate text-xs' />
        )}
      </button>
    </div>
  )

  return <>{eventElements}</>
}
